package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.admin.UserPhotos
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

/** Что именно выбрано к удалению. */
private data class PhotoSelection(
    /** "PROFILE" | "CARD" */
    val target: String,
    /** id карточки при target = CARD */
    val cardId: String? = null,
)

/**
 * Диалог точечной модерации фото: подгружает ВСЕ фото юзера (профильное +
 * аватары карточек), админ выбирает одно, затем причину. Удаляется только
 * выбранное; карточка при удалении её фото удаляется целиком. После успеха
 * список обновляется — можно удалить следующее фото, не закрывая диалог.
 */
@Composable
fun AvatarModerationDialog(
    userId: String,
    userName: String,
    repo: AdminRepository,
    onDismiss: () -> Unit,
    onRemoved: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var photos by remember { mutableStateOf<UserPhotos?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var selected by remember { mutableStateOf<PhotoSelection?>(null) }
    var working by remember { mutableStateOf(false) }
    var errorMsg by remember { mutableStateOf<String?>(null) }
    var successMsg by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }

    LaunchedEffect(userId, reloadKey) {
        loadError = null
        try {
            photos = repo.getUserPhotos(userId)
        } catch (e: Throwable) {
            loadError = e.message ?: "Не удалось загрузить фото"
        }
    }

    // Скрим
    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(600)
            .styleModifier {
                property("background", AppColors.Ink.toRgbaString(0.40f))
                property("backdrop-filter", "blur(2px)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .onClick { if (!working) onDismiss() }
    ) {
        // Диалог
        Box(
            Modifier
                .sBorderRadius(16)
                .styleModifier {
                    property("background", AppColors.Surface.toRgbaString(1f))
                    property(
                        "box-shadow",
                        "0 8px 40px -8px ${AppColors.Ink.toRgbaString(0.24f)}, " +
                            "0 2px 8px ${AppColors.Ink.toRgbaString(0.08f)}"
                    )
                    property("padding", "28px")
                    property("max-width", "480px")
                    property("width", "calc(100vw - 48px)")
                    property("max-height", "calc(100vh - 64px)")
                    property("overflow-y", "auto")
                    property("position", "relative")
                }
                .onClick { it.stopPropagation() }
        ) {
            Column(Modifier.fillMaxWidth()) {
                SpanText(
                    "Модерация фото",
                    Modifier.bodyMMedium().color(AppColors.Ink)
                        .styleModifier {
                            property("font-size", "16px")
                            property("font-weight", "700")
                        }
                )
                Box(Modifier.height(10.px))
                SpanText(
                    "Выберите фото юзера $userName, которое нужно удалить. " +
                        "Удалится только выбранное; фото карточки удаляется вместе с карточкой.",
                    Modifier.bodyMRegular().color(AppColors.Muted)
                )

                Box(Modifier.height(14.px))

                val loaded = photos
                when {
                    loadError != null -> SpanText(loadError!!, Modifier.bodySMedium().color(AppColors.Error))
                    loaded != null -> {
                        val hasAny = loaded.avatarUrl != null || loaded.cards.any { it.avatarUrl != null }
                        if (!hasAny) {
                            SpanText("У юзера нет фото.", Modifier.bodySMedium().color(AppColors.Muted))
                        } else {
                            PhotoGrid(
                                photos = loaded,
                                selected = selected,
                                enabled = !working,
                                onSelect = { selected = it; errorMsg = null; successMsg = null },
                            )
                        }
                    }
                }

                Box(Modifier.height(14.px))
                Box(
                    Modifier.fillMaxWidth().sBorderRadius(8).styleModifier {
                        property("background", AppColors.Accent.WarningTint.toRgbaString(1f))
                        property("border", "1px solid ${AppColors.Warning.toRgbaString(0.22f)}")
                        property("padding", "10px 12px")
                    }
                ) {
                    SpanText(
                        "Выберите фото и причину — она войдёт в уведомление юзеру " +
                            "(повторные нарушения — удаление аккаунта).",
                        Modifier.bodySMedium().color(AppColors.Warning)
                    )
                }

                if (errorMsg != null) {
                    Box(Modifier.height(10.px))
                    SpanText(errorMsg!!, Modifier.bodySMedium().color(AppColors.Error))
                }
                if (successMsg != null) {
                    Box(Modifier.height(10.px))
                    SpanText(successMsg!!, Modifier.bodySMedium().color(AppColors.Success))
                }

                Box(Modifier.height(20.px))

                val sel = selected
                val choose: (String) -> Unit = { reason ->
                    val target = selected
                    if (target != null && !working) {
                        working = true
                        errorMsg = null
                        successMsg = null
                        scope.launch {
                            try {
                                repo.removeUserAvatar(userId, reason, target.target, target.cardId)
                                successMsg =
                                    if (target.target == "CARD") {
                                        "Карточка удалена, юзер уведомлён"
                                    } else {
                                        "Фото удалено, юзер уведомлён"
                                    }
                                selected = null
                                onRemoved()
                                reloadKey++
                            } catch (e: Throwable) {
                                errorMsg = e.message ?: "Ошибка запроса"
                            } finally {
                                working = false
                            }
                        }
                    }
                }

                Column(Modifier.fillMaxWidth()) {
                    ModerationReasonButton(
                        label = "На фото нет человека",
                        enabled = sel != null && !working,
                        onClick = { choose("NO_PERSON") },
                    )
                    Box(Modifier.height(8.px))
                    ModerationReasonButton(
                        label = "Неприемлемый контент",
                        enabled = sel != null && !working,
                        onClick = { choose("INAPPROPRIATE") },
                    )
                }

                Box(Modifier.height(16.px))

                Box(
                    Modifier
                        .sBorderRadius(999)
                        .padding(leftRight = 18.px, topBottom = 9.px)
                        .cursor(if (!working) Cursor.Pointer else Cursor.NotAllowed)
                        .styleModifier {
                            property("background", AppColors.Surface.toRgbaString(1f))
                            property("border", "1px solid ${AppColors.Ink.toRgbaString(0.10f)}")
                            property("align-self", "flex-start")
                        }
                        .onClick { if (!working) onDismiss() }
                ) {
                    SpanText(
                        if (successMsg != null) "Закрыть" else "Отмена",
                        Modifier.bodySMedium().color(AppColors.Ink)
                            .styleModifier { property("font-weight", "600") }
                    )
                }
            }
        }
    }
}

@Composable
private fun PhotoGrid(
    photos: UserPhotos,
    selected: PhotoSelection?,
    enabled: Boolean,
    onSelect: (PhotoSelection) -> Unit,
) {
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("gap", "10px")
            }
            .toAttrs()
    ) {
        photos.avatarUrl?.let { url ->
            PhotoThumb(
                url = url,
                caption = "Фото профиля",
                isSelected = selected?.target == "PROFILE",
                enabled = enabled,
                onClick = { onSelect(PhotoSelection("PROFILE")) },
            )
        }
        photos.cards.forEach { card ->
            card.avatarUrl?.let { url ->
                PhotoThumb(
                    url = url,
                    caption = card.templateName,
                    isSelected = selected?.cardId == card.cardId,
                    enabled = enabled,
                    onClick = { onSelect(PhotoSelection("CARD", card.cardId)) },
                )
            }
        }
    }
}

@Composable
private fun PhotoThumb(
    url: String,
    caption: String,
    isSelected: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Div(
        attrs = Modifier
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .classNames("na-press")
            .styleModifier {
                property("width", "88px")
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("gap", "6px")
            }
            .onClick { if (enabled) onClick() }
            .toAttrs()
    ) {
        Div(
            attrs = Modifier
                .sBorderRadius(12)
                .styleModifier {
                    property("width", "88px")
                    property("height", "88px")
                    property("overflow", "hidden")
                    property("background", AppColors.Ink.toRgbaString(0.04f))
                    property(
                        "border",
                        if (isSelected) {
                            "2px solid ${AppColors.Error.toRgbaString(0.90f)}"
                        } else {
                            "2px solid ${AppColors.Ink.toRgbaString(0.10f)}"
                        },
                    )
                    property("transition", "border-color 120ms ease")
                }
                .toAttrs()
        ) {
            Img(
                src = url,
                attrs = {
                    attr("loading", "lazy")
                    attr("alt", caption)
                    style {
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                    }
                }
            )
        }
        SpanText(
            caption,
            Modifier.bodySMedium()
                .color(if (isSelected) AppColors.Ink else AppColors.Muted)
                .styleModifier {
                    property("font-size", "11px")
                    property("text-align", "center")
                    property("max-width", "88px")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
        )
    }
}

@Composable
internal fun ModerationReasonButton(
    label: String,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(10)
            .padding(leftRight = 16.px, topBottom = 12.px)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .classNames("na-press")
            .styleModifier {
                property("background", AppColors.Error.toRgbaString(0.10f))
                property("border", "1px solid ${AppColors.Error.toRgbaString(0.28f)}")
                if (!enabled) property("opacity", "0.5")
            }
            .onClick { if (enabled) onClick() }
    ) {
        SpanText(
            label,
            Modifier.bodySMedium().color(AppColors.Error)
                .styleModifier { property("font-weight", "600") }
        )
    }
}
